<template>
    <!-- Private to the candidate: computed in the browser from their own results, never stored -->
    <section
        v-if="showsBlock"
        class="my-6 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-default dark:bg-surface-subtle"
    >
        <h3 class="mb-2 font-semibold text-gray-900 dark:text-txt-headings">🧭 Ako ti táto práca sedí</h3>

        <template v-if="!userId">
            <p class="mb-3 text-sm text-gray-700 dark:text-txt-body">
                Firma opísala, ako sa na tejto pozícii pracuje. Prihlás sa a podľa svojich testov sebapoznania
                uvidíš, čo ti na nej sedí a na čo sa pripraviť.
            </p>
            <CButton size="small" variant="primary" @click="openLogin()">Prihlásiť sa</CButton>
        </template>

        <template v-else-if="state === 'done'">
            <JobFitPoints v-if="points.length" audience="candidate" :points="points" />
            <p v-else class="text-sm text-gray-700 dark:text-txt-body">
                Firma opísala, ako sa na tejto pozícii pracuje. Doplň test a uvidíš, čo ti na nej sedí.
            </p>

            <p v-if="points.length" class="mt-3 text-xs text-gray-500 dark:text-txt-body">
                Toto vidíš len ty. Firma tieto body uvidí, len ak k prihláške pridáš výsledky, z ktorých vznikli.
            </p>

            <NextTestForJob class="mt-3" :done-types="doneTypes" :work-profile="job.workProfile" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { getJobFit, hasWorkProfile } from '#shared/sebapoznanie/jobFit'
import type { Job } from '#shared/types/Job'
import type { SharedAssessment } from '#shared/types/JobRequest'
import type { LoadingState } from '#shared/types/LoadingState'
import JobFitPoints from '@/components/jobs/fit/JobFitPoints.vue'
import NextTestForJob from '@/components/jobs/fit/NextTestForJob.vue'
import { useSharedAssessments } from '@/composables/jobRequests/useSharedAssessments'
import { useAuthModal } from '@/composables/useAuthModal'
import { useApplicationStore } from '@/stores/application'

const props = defineProps<{ job: Job }>()

const store = useApplicationStore()
const { openLogin } = useAuthModal()
const { buildSharedAssessments } = useSharedAssessments()

const state = ref<LoadingState>('idle')
const assessments = ref<SharedAssessment[]>([])

const userId = computed(() => store.userId)
const showsBlock = computed(() => hasWorkProfile(props.job.workProfile) && state.value !== 'error')
const points = computed(() => getJobFit(props.job.workProfile, assessments.value, 'candidate'))
const doneTypes = computed(() => assessments.value.map((assessment) => assessment.type))

async function load() {
    if (!userId.value || !hasWorkProfile(props.job.workProfile)) return

    state.value = 'loading'
    try {
        assessments.value = await buildSharedAssessments(userId.value)
        state.value = 'done'
    } catch (error) {
        console.error('[JobFitForCandidate]: Could not load results:', error)
        state.value = 'error'
    }
}

onMounted(load)
watch(userId, load)
</script>
